import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Search } from '@mui/icons-material';
import { Box, capitalize } from '@mui/material';
import { common } from '@mui/material/colors';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { FuseSelect, ListPage, ListRow } from '/src/components';
import { useFilterStore, useParticipationStore, useUserStore } from '/src/context';
import { DataModelType } from '/src/lib/types';
import { currency, percent } from '/src/utils';

/**
 * List Participations Page
 */
export const ListParticipationsPage = observer(() => {
  const defaultSortType = 'name';
  const defaultSortReverse = false;

  /** Context **/
  const participationStore = useParticipationStore();
  const filterStore = useFilterStore();
  const userStore = useUserStore();
  const navigate = useNavigate();

  /** State **/
  const [search, setSearch] = useState<Record<string, any>>({});
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);

  /** Computed **/
  const participations = participationStore.participations;
  const isFiltered = !!Object.values(search).length;

  const lenders = useMemo(() => filterStore.lenderListGet(), [filterStore._lenderList]);
  const loans = useMemo(() => filterStore.loanListGet(), [filterStore._loanList]);

  const listData = useMemo(() => {
    if (participations) {
      let list = participations.toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
      if (sortReverse) list.reverse();

      if (search.loanId || search.lenderId) {
        list = list.filter((participation) => {
          const loanMatch = search.loanId ? participation.loan._id === search.loanId : true;
          const lenderMatch = search.lenderId ? participation.lender?._id === search.lenderId : true;
          return loanMatch && lenderMatch;
        });
      }

      return list;
    }
  }, [participations, search, sortType, sortReverse]);

  /** Methods **/

  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    participationStore.query();
  }, []);

  /** Render **/
  return (
    <ListPage
      data={listData}
      modelType={DataModelType.Participation}
      headerItems={[
        { flex: 5, propName: '', label: '' },
        { flex: 1, propName: 'lender.name', label: 'Investor' },
        { flex: 1, propName: 'loan.name', label: 'Deal Name' },
        { flex: 1, propName: 'participationPercentage', label: 'Participation Percentage', textAlign: 'right' },
        { flex: 1, propName: 'relativeBalance', label: 'Current Balance', textAlign: 'right' },
        { flex: 1, propName: 'feeBalance', label: 'Fees Due', textAlign: 'right' },
        { flex: 1, propName: 'interestBalance', label: 'Interest Due', textAlign: 'right' },
        { flex: 1, propName: 'loan.status', label: 'Deal Status', textAlign: 'right' },
      ]}
      toolbarItems={
        <>
          {userStore.isAdmin && (
            <Box display="flex" flex={1}>
              <Box display="flex" justifyContent="start" alignItems="center" gap="3px">
                <Search />
                <FuseSelect
                  placeholder="All Investors"
                  ariaLabel="View Deals By Investor"
                  value={search.lenderId}
                  updateValue={(lenderId) => setSearch({ ...search, lenderId })}
                  options={(Array.isArray(lenders) ? lenders : [])?.map((lender) => ({
                    label: lender.name,
                    value: lender._id,
                  }))}
                  enablePlaceholderOption
                  disableUnderline
                  disableMargin
                  color={common.white}
                />
              </Box>
            </Box>
          )}
          {(userStore.isAdmin || userStore.isLender) && (
            <Box display="flex" flex={1}>
              <Box display="flex" justifyContent="start" alignItems="center" gap="3px">
                <Search />
                <FuseSelect
                  placeholder="All Deals"
                  ariaLabel="View Deals By Investor"
                  value={search.loanId}
                  updateValue={(loanId) => setSearch({ ...search, loanId })}
                  options={(Array.isArray(loans) ? loans : [])?.map((loan) => ({
                    label: loan.name,
                    value: loan._id,
                  }))}
                  enablePlaceholderOption
                  disableUnderline
                  disableMargin
                  color={common.white}
                />
              </Box>
            </Box>
          )}
        </>
      }
      isFiltered={isFiltered}
      sortType={sortType}
      sortReverse={sortReverse}
      sortList={sortList}
      noDataMessage="You're not participating in any Deals yet."
      hideCreateButton
    >
      {listData?.map((participation, i) => (
        <ListRow
          key={i}
          items={[
            {
              flex: 5,
              propName: '',
              icon: <Edit />,
              isButton: true,
              tooltip: 'Edit Deal',
              onClick: () => navigate(`/deals/${participation.loan._id}/edit`),
            },
            {
              flex: 1,
              propName: 'lender.name',
              content: participation.lender?.name,
              url: `/investors/${participation.lender?._id}`,
            },
            {
              flex: 1,
              propName: 'loan.name',
              content: participation.loan.name,
              url: `/deals/${participation.loan._id}`,
            },
            {
              flex: 1,
              propName: 'participationPercentage',
              content: percent.format(participation.participationPercentage),
              textAlign: 'right',
            },
            {
              flex: 1,
              propName: 'relativeBalance',
              content: currency.format(participation.relativeBalance),
              textAlign: 'right',
            },
            {
              flex: 1,
              propName: 'feeBalance',
              content: currency.format(participation.loan.feeBalance * participation.participationPercentage),
              textAlign: 'right',
            },
            {
              flex: 1,
              propName: 'interestBalance',
              content: currency.format(participation.loan.interestBalance * participation.participationPercentage),
              textAlign: 'right',
            },
            { flex: 1, propName: 'loan.status', content: capitalize(participation.loan.status), textAlign: 'right' },
          ]}
          rowIndex={i}
        />
      ))}
    </ListPage>
  );
});
